import { useEffect, useState } from 'react';
import { useNotificationsSocket } from '../../hooks/useNotificationsSocket';
import { useNotificationSound, type Tier } from '../../hooks/useNotificationSound';

/**
 * Cross-tab leader-elected, route-wide audio cue for prediction
 * notifications.
 *
 * Why this exists:
 *   The notifications page mounts `useNotificationSound` so fills
 *   ring while the operator is looking at the feed. But the operator
 *   spends most of their time on other pages (Opportunities,
 *   Positions, Unwinding) — and on the prior wiring, navigating away
 *   from `/predictions/notifications` silenced every cue. This manager
 *   lifts the sound side-effect into a top-level component so alarms,
 *   fills (automation + opener + manual), and TNA negatives play on
 *   every authenticated page.
 *
 * Cross-tab dedupe:
 *   The Web Locks API (`navigator.locks.request`) gives us a free
 *   exclusive-lock primitive — exactly one tab holds the lock at a
 *   time, the lock auto-releases when the tab crashes/closes/sleeps,
 *   and waiting tabs take over within ms. We use it as a leader
 *   election: only the leader mounts `useNotificationSound`, every
 *   other tab is silent. Two tabs open → one ring, not two.
 *
 *   **Lock is held for the tab's lifetime — NOT released on
 *   visibility-hidden.** The earlier wiring released the lock when
 *   the tab went background and re-acquired it on foreground, hoping
 *   to "make sound follow the active tab" across multiple Predictions
 *   tabs. In practice operators run one Predictions tab in the
 *   background while they're on another (the exchange UI), so the
 *   visibility-release just silenced every event arriving while the
 *   operator wasn't looking at the dashboard. First-acquired wins,
 *   and the lock only releases when the tab closes / crashes / the
 *   component unmounts — at which point a waiting tab takes over.
 *
 *   Browser support: Web Locks ships in every evergreen browser
 *   (Chrome/Edge/Firefox/Safari 15.4+). On the rare case where it's
 *   missing (very old Safari, locked-down enterprise wrapper) we
 *   degrade to "every tab plays" — same behavior as before this
 *   change, never worse.
 *
 *   The socket connection is *only* opened by the leader (we mount
 *   `useNotificationsSocket` inside the leader child component). A
 *   follower tab opens zero notification sockets — no extra fan-out
 *   cost on the gateway when an operator has 5 tabs open.
 *
 * Autoplay policy:
 *   The first audio load before any user gesture is silently blocked
 *   by every modern browser. The hook itself catches the load error
 *   and self-recovers on the next event after a user click. Nothing
 *   to do here — flagging it because it surprised us during the
 *   initial sound rollout.
 */
const GLOBAL_TIERS: ReadonlySet<Tier> = new Set<Tier>([
  'alarm',
  'execution',
  'tna',
]);

const LOCK_NAME = 'notification-sound-leader';

function hasWebLocks(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'locks' in navigator &&
    typeof (navigator as Navigator & { locks?: unknown }).locks === 'object'
  );
}

/**
 * Inner component — only mounted when this tab is the elected leader.
 * Opens the notifications socket + plays the three global tiers.
 *
 * Acknowledging the event is required so the same `latestEvent`
 * doesn't replay on an unrelated re-render — the hook itself also
 * guards on `event.id`, but acknowledging is the explicit contract.
 */
function LeaderAudio(): null {
  const { latestEvent, acknowledge } = useNotificationsSocket();
  useNotificationSound(latestEvent, GLOBAL_TIERS);

  useEffect(() => {
    if (!latestEvent) return;
    acknowledge();
  }, [latestEvent, acknowledge]);

  return null;
}

export function NotificationSoundManager(): JSX.Element | null {
  const [isLeader, setIsLeader] = useState(false);

  useEffect(() => {
    // No Web Locks API → fall back to "every tab is its own leader".
    // Matches the pre-leader-election behavior; we lose cross-tab
    // dedupe but never lose sound altogether.
    if (!hasWebLocks()) {
      setIsLeader(true);
      return;
    }

    // `release` is the resolver for the promise we hand to
    // `navigator.locks.request` — calling it lets the next waiting
    // tab acquire the lock. We only call it from the unmount cleanup;
    // visibility changes do NOT release the lock (see file header).
    let release: (() => void) | null = null;
    let cancelled = false;

    // `request` resolves when the callback's returned promise settles,
    // so we hand back a promise that resolves only when `release` is
    // called. While we're inside the callback, this tab IS the leader.
    void navigator.locks
      .request(LOCK_NAME, { mode: 'exclusive' }, () => {
        if (cancelled) return Promise.resolve();
        setIsLeader(true);
        return new Promise<void>((resolve) => {
          release = () => {
            release = null;
            resolve();
          };
        });
      })
      .then(() => {
        // Lock released because the effect unmounted (tab navigating
        // away from the React tree, or HMR). Drop leader state so the
        // inner audio component unmounts and stops every player +
        // closes the WS.
        if (!cancelled) setIsLeader(false);
      });

    return () => {
      cancelled = true;
      if (release !== null) release();
    };
  }, []);

  return isLeader ? <LeaderAudio /> : null;
}
